import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Shield, Unlock, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  useProjectRetainage,
  useProjectRetainageReleases,
  useInitiateProjectRetainageRelease,
} from "@/hooks/useRetainageReleases";
import { toast } from "sonner";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  projectId: string;
  projectName?: string;
  projectStatus?: string;
  retainageReleasedAt?: string | null;
}

export const RetainageDashboardCard = ({
  projectId,
  projectName,
  projectStatus,
  retainageReleasedAt,
}: Props) => {
  const { data: workOrders } = useProjectRetainage(projectId);
  const { data: releases } = useProjectRetainageReleases(projectId);
  const initiateRelease = useInitiateProjectRetainageRelease();

  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState("");

  if (!workOrders?.length) return null;

  const wosWithRetainage = workOrders.filter((wo: any) => {
    const held = Number(wo.amount) * (Number(wo.retainage_pct ?? 10) / 100);
    return held > 0;
  });

  if (!wosWithRetainage.length) return null;

  const totalHeld = wosWithRetainage.reduce((s: number, wo: any) => {
    const held = Number(wo.amount) * (Number(wo.retainage_pct ?? 10) / 100);
    return s + held;
  }, 0);

  const totalReleased = wosWithRetainage.reduce(
    (s: number, wo: any) => s + Number(wo.retainage_released ?? 0),
    0
  );

  const pendingRelease = totalHeld - totalReleased;
  const hasPendingRelease = (releases ?? []).some((r: any) => r.status === "pending");
  const canRelease =
    projectStatus === "completed" && !retainageReleasedAt && pendingRelease > 0 && !hasPendingRelease;

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      holding: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      partial_release: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      released: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    };
    return map[status] ?? "bg-muted text-muted-foreground";
  };

  const handleInitiateRelease = async () => {
    try {
      await initiateRelease.mutateAsync({
        projectId,
        amount: pendingRelease,
        notes: releaseNotes || undefined,
      });
      toast.success("Retainage release submitted for approval");
      setReleaseOpen(false);
      setReleaseNotes("");
    } catch (e: any) {
      toast.error(e.message || "Failed to initiate retainage release");
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" /> Retainage Held
              {projectName && (
                <span className="text-muted-foreground font-normal">— {projectName}</span>
              )}
            </CardTitle>
            {retainageReleasedAt ? (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckCircle2 className="mr-1 h-3 w-3" /> Released
              </Badge>
            ) : hasPendingRelease ? (
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                <AlertCircle className="mr-1 h-3 w-3" /> Pending Approval
              </Badge>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {wosWithRetainage.map((wo: any) => {
            const held = Number(wo.amount) * (Number(wo.retainage_pct ?? 10) / 100);
            const released = Number(wo.retainage_released ?? 0);
            const woBalance = held - released;
            const name = wo.vendors?.name ?? wo.number;

            return (
              <div key={wo.id} className="flex items-center justify-between text-sm">
                <span className="truncate max-w-[180px]">{name}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium">{fmt(woBalance)}</span>
                  <Badge className={`text-[10px] ${statusBadge(wo.retainage_status ?? "holding")}`}>
                    {(wo.retainage_status ?? "holding").replace(/_/g, " ")}
                  </Badge>
                  {released > 0 && (
                    <span className="text-xs text-muted-foreground">← {fmt(released)} released</span>
                  )}
                </div>
              </div>
            );
          })}

          <Separator className="my-2" />

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-muted-foreground">Total held</p>
              <p className="font-semibold">{fmt(totalHeld)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Released</p>
              <p className="font-semibold text-green-600">{fmt(totalReleased)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Pending</p>
              <p className="font-semibold">{fmt(pendingRelease)}</p>
            </div>
          </div>

          {canRelease && (
            <>
              <Separator className="my-2" />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setReleaseOpen(true)}
              >
                <Unlock className="mr-1 h-4 w-4" /> Release Retainage
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Release Retainage Dialog */}
      <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release Retainage</DialogTitle>
            <DialogDescription>
              This will create a retainage release invoice and route it through the approval queue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Release Amount</Label>
              <Input value={fmt(pendingRelease)} disabled className="font-mono" />
              <p className="text-xs text-muted-foreground">
                Full remaining retainage balance for this project.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder="Reason for release, punch list status, etc."
                rows={3}
              />
            </div>
            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">What happens next:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>A retainage release invoice (type: RET) will be created</li>
                <li>An approval request will be sent to Admin/Controller users</li>
                <li>Once approved, the retainage will be marked as released</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseOpen(false)}>Cancel</Button>
            <Button onClick={handleInitiateRelease} disabled={initiateRelease.isPending}>
              {initiateRelease.isPending ? "Submitting…" : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
